/* eslint-disable react-hooks/exhaustive-deps */
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { Avatar, Button, Dropdown, Select, Switch } from "antd";
import Input from "antd/es/input/Input";
import { ColumnsType } from "antd/es/table";
import { useEffect, useRef, useState } from "react";
import AddIcon from "../../../assets/icons/AddIcon";
import CaretDownIcon from "../../../assets/icons/CaretDownIcon";
import EditButtonIcon from "../../../assets/icons/EditButtonIcon";
import ThreeDotsIcon from "../../../assets/icons/ThreeDotsIcon";
import SuccessModal from "../../../components/common/RoleManagement/SuccessModal";
import AddNewUserModal from "../../../components/common/UserManagement/AddNewUserModal";
import CustomTable from "../../../components/ui/CustomTable";
import {
  useGetAllDepartmentQuery,
  useGetAllEmployeesQuery,
  useGetAllSubDepartmentQuery,
  useGetSubDepartmentByIdGroupChatQuery,
  useUpdateEmployeeStatusMutation,
} from "../../../features/superAdmin/employeeService/employeeManagementService";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import {
  GetAllEmployeesParamsType,
  GetAllEmployeesResponseType,
} from "../../../types";
import { capitalize } from "../../../utils/string";

export function UpdateUserStatus({
  status: initialStatus,
  id,
}: {
  status: "active" | "inactive" | string;
  id: string;
}) {
  const [status, setStatus] = useState<"active" | "inactive">("active");
  const [open, setOpen] = useState(false);
  const hasShownSuccess = useRef(false);

  const [updateStatus, { isSuccess, isError, error }] =
    useUpdateEmployeeStatusMutation();

  useError(isError, error, "User status update failed");

  useEffect(() => {
    setStatus(initialStatus as "active" | "inactive");
  }, [initialStatus]);

  useEffect(() => {
    if (isSuccess && hasShownSuccess.current) {
      setOpen(true);
    }
  }, [isSuccess]);

  const handleToggleStatus = () => {
    const newStatus = status === "active" ? "inactive" : "active";
    setStatus(newStatus);
    updateStatus({ data: { status: newStatus }, id });
    hasShownSuccess.current = true;
  };

  const handleCloseModal = () => {
    setOpen(false);
    hasShownSuccess.current = false;
  };

  return (
    <div className="flex items-center gap-2 justify-between">
      <Switch
        checked={status === "active"}
        onChange={handleToggleStatus}
        size="small"
      />
      <div>{status === "active" ? "Active" : "Inactive"}</div>

      <SuccessModal
        open={open}
        handleClose={handleCloseModal}
        heading="Status Updated Successfully"
        subHeading={`The user status has been changed to "${status.toUpperCase()}".`}
      />
    </div>
  );
}

export default function User() {
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<
    number | null
  >(null);
  const {
    page,
    limit,
    handlePagination,
    handleFilter,
    filter,
    handleSearchEnter,
    keyword,
    handleLimit,
  } = useTable<GetAllEmployeesParamsType>({
    defaultFilter: {
      status: undefined,
    },
  });
  const { open, handleOpen, handleClose } = useModal();

  const {
    isLoading,
    isError,
    error,
    data: allEmployeesData,
  } = useGetAllEmployeesQuery({
    params: {
      ...filter,
      skip: undefined,
      page,
      sort: "createdAt:desc",
      search: keyword,
      status: filter.status === "all" ? undefined : filter.status,
    },
  });

  const {
    isError: allDepartmentIsError,
    error: allDepartmentError,
    data: allDepartmentData,
  } = useGetAllDepartmentQuery({
    params: {
      page: 1,
      limit: 100,
      status: "active",
    },
  });
  const id = selectedDepartmentId ? selectedDepartmentId : skipToken;

  const {
    isError: allSubDepartmentIsError,
    error: allSubDepartmentError,
    data: allSubDepartmentData,
  } = useGetAllSubDepartmentQuery({
    params: {
      page: 1,
      limit: 100,
      status: "active",
    },
  });

  const {
    isError: subDepartmentIsError,
    error: subDepartmentError,
    data: subDepartmentData,
  } = useGetSubDepartmentByIdGroupChatQuery(id);

  useError(
    allSubDepartmentIsError,
    allSubDepartmentError,
    "Get all sub department list fail",
  );

  useError(isError, error, "Get all user list fail");
  useError(
    allDepartmentIsError,
    allDepartmentError,
    "Get all department list fail",
  );
  useError(
    subDepartmentIsError,
    subDepartmentError,

    "Get all sub department list fail",
  );
  const employeesData = allEmployeesData?.data?.employees;

  const columns: ColumnsType<GetAllEmployeesResponseType> = [
    {
      title: "Employee ID",
      dataIndex: "officialInfo",
      render: (val) => val?.employee_id ?? "n/a",
    },
    {
      title: "Employee Name",
      render: (val) => {
        return (
          <div className="flex gap-2 items-center">
            <Avatar src={val?.personalInfo?.pro_pic} />
            <div>{val?.officialInfo?.name}</div>
          </div>
        );
      },
    },
    {
      title: "Department",
      dataIndex: "officialInfo",
      render: (val) => val?.department?.name ?? "n/a",
    },
    {
      title: "Sub-Department",
      dataIndex: "officialInfo",
      render: (val) => val?.sub_department?.name ?? "n/a",
    },
    {
      title: "Email",
      dataIndex: "officialInfo",
      render: (val) => val?.email ?? "n/a",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <div
          className={`px-2 rounded-full inline-block ${
            status === "active"
              ? "text-[#1DB469] bg-[#F2FFF9]"
              : "text-[#FF3838] bg-[#FFDCDA]"
          }`}
        >
          {capitalize(status)}
        </div>
      ),
    },
    {
      title: "Action",
      // key: "action",
      render: (val) => {
        // if(val !== userStatus){
        //   setUserStatus((prev) => (prev === "active" ? "inactive" : "active"));
        // }
        return (
          <div className="flex justify-end">
            <Dropdown
              placement="bottomRight"
              menu={{
                items: [
                  {
                    key: "0",
                    label: <UpdateUserStatus id={val.id} status={val.status} />,
                  },
                  // {
                  //   key: "1",
                  //   label: (
                  //     <Link to="/company/employee-management/employee/123">
                  //       <div className="flex gap-1 items-center">
                  //         <div>
                  //           <ViewIcon />
                  //         </div>
                  //         <div>View Details</div>
                  //       </div>
                  //     </Link>
                  //   ),
                  // },
                  {
                    key: "2",
                    label: (
                      <div
                        className="flex gap-1 items-center"
                        onClick={() =>
                          handleOpen({ type: "UPDATE", data: val })
                        }
                      >
                        <div>
                          <EditButtonIcon />
                        </div>
                        <div>Edit</div>
                      </div>
                    ),
                  },
                ],
              }}
              trigger={["click"]}
            >
              <div className="cursor-pointer">
                <ThreeDotsIcon />
              </div>
            </Dropdown>
          </div>
        );
      },
    },
  ];

  return (
    <SidebarLayout title="User List">
      {(open.type === "ADD" || open.type === "UPDATE") && (
        <AddNewUserModal
          open={open.open}
          type={open?.type}
          data={open?.data}
          handleClose={handleClose}
        />
      )}
      <div className="flex gap-2 items-center justify-between mb-6">
        <div className="flex gap-2 items-center">
          <div className="text-nowrap">Total Rows</div>
          <Select
            placeholder="10"
            suffixIcon={<CaretDownIcon />}
            size="small"
            style={{ width: 70 }}
            onChange={handleLimit}
            options={[
              { label: "10", value: 10 },
              { label: "20", value: 20 },
              { label: "50", value: 50 },
              { label: "100", value: 100 },
              { label: "1000", value: 1000 },
            ]}
          />
          <div className="text-nowrap">
            of {allEmployeesData?.data?.total || 0}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Input
            value={keyword || undefined}
            onChange={(e) => handleSearchEnter(e.currentTarget.value)}
            placeholder="Search"
            size="small"
          />
          <Select
            className="!min-w-40"
            placeholder="Department"
            suffixIcon={<CaretDownIcon />}
            size="small"
            value={filter?.department ?? undefined}
            onChange={(value) => {
              handleFilter("department", value);
              handleFilter("sub_department", undefined);
              setSelectedDepartmentId(
                value !== undefined ? Number(value) : null,
              );
            }}
            options={allDepartmentData?.data.map((dep: any) => ({
              label: dep.name,
              value: dep.id,
            }))}
            allowClear
          />
          <Select
            className="!min-w-40"
            placeholder="Sub Department"
            suffixIcon={<CaretDownIcon />}
            size="small"
            value={filter?.sub_department ?? undefined}
            onChange={(value) => handleFilter("sub_department", value)}
            options={subDepartmentData?.data.map((dep: any) => ({
              label: dep.name,
              value: dep.id,
            }))}
            allowClear
            disabled={!selectedDepartmentId}
          />
          <Select
            className="!min-w-40"
            placeholder="Status"
            suffixIcon={<CaretDownIcon />}
            size="small"
            value={filter.status ?? undefined}
            onChange={(value) => handleFilter("status", value)}
            options={[
              { label: "All", value: "all" },
              { label: "Active", value: "active" },
              { label: "Inactive", value: "inactive" },
            ]}
            allowClear
          />

          <Button
            className="flex items-center gap-2 border-[#FFD3C0] bg-[#FFF1EB] text-primary"
            style={{ padding: "2px 10px" }}
            onClick={() => handleOpen({ type: "ADD" })}
          >
            <span>
              <AddIcon />
            </span>
            <span>Add new user</span>
          </Button>
        </div>
      </div>
      <CustomTable
        className="designationTable"
        limit={limit}
        page={page}
        columns={columns}
        dataSource={employeesData}
        loading={isLoading}
        total={allEmployeesData?.data?.total || 0}
        handlePagination={handlePagination}
        filterSection={<></>}
      />
    </SidebarLayout>
  );
}
